import React from "react";
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Switch,
  Typography,
  message,
} from "antd";
import moment from "moment-timezone";
import { countries } from "../../../../../../../../shared-components/countries";
import currencyData from "../../../../../../../../shared-components/currency.json";
import { shiftCodesWithNames } from "../../../../../../../../Global/GPTComponents/randomColors";
import DownloadDocIcon from "../../../../../../../../Icons/DownloadDocIcon";
import { useSelector, useDispatch } from "react-redux";
import {
  updateDimensionAction,
  getSingleDimensionAction,
} from "../../../../../../../../store/action/admin/dimensionsEntityActions";

const { Text } = Typography;
const { TextArea } = Input;

const BranchIcons = [
  { value: "House", label: "House" },
  { value: "Car", label: "Car" },
  { value: "Book", label: "Book" },
  { value: "Dollar", label: "Dollar" },
  { value: "File", label: "File" },
];

const CreateBranchLocation = React.forwardRef(
  ({ onClose, open, isCreateMode }, ref) => {
    const dispatch = useDispatch();
    const themeDefault = useSelector((state) => state.themeDefault);
    const singleEntityState = useSelector((state) => state.getSingleEntityList);
    const getSingleDimensionList = useSelector(
      (state) => state.getSingleDimensionList
    );
    const entityId = singleEntityState?.info?._id;
    const dimensionId = getSingleDimensionList?.info?.dimension?._id;
    const [form] = Form.useForm();

    const handleCountryChange = () => {
      form?.setFieldsValue?.({ currency: undefined, timezone: undefined });
    };

    const handleCreate = async () => {
      try {
        const values = await form.validateFields();

        const payload = {
          type: "branch",
          entityId,
          branchId: values.branchId,
          name: values.branchName,
          branchIcon: values.branchIcon,
          branchColor: values.branchColor,
          description: values.description,
          enableHierarchy: Boolean(values.enableHierarchy),
          mutuallyExclusive: Boolean(values.mutuallyExclusive),
          mandatory: Boolean(values.mandatory),
          enable_regional_per_dimension: Boolean(
            values.enableRegionalPerDimension
          ),
          country: values.country,
          currency: values.currency,
          timezone: values.timezone,
          language: values.language,
          use_for_financial_consolidation: Boolean(
            values.enableFinancialConsolidation
          ),
          enableAsFilter: Boolean(values.enableAsFilter),
          internal_notes: values.internalNotes,
          attachments: [],
          meta: { status: "created" },
        };

        // If update mode, dispatch update action
        if (!isCreateMode) {
          if (!dimensionId) {
            message.error("Dimension ID is required for update");
            throw new Error("Dimension ID is required for update");
          }
          await dispatch(updateDimensionAction(dimensionId, payload));
          message.success("Branch Location updated successfully");
          // Refresh the dimension data to populate fields correctly
          await dispatch(getSingleDimensionAction(dimensionId));
          // Don't reset form or close drawer - let parent handle step navigation
          // Return payload so parent can continue
          return payload;
        }

        form.resetFields();
        return payload;
      } catch (error) {
        console.error("Form validation failed:", error);
        if (!isCreateMode) {
          const errMsg =
            error?.message || error || "Failed to update branch location";
          message.error(errMsg);
          throw error;
        }
        throw error;
      }
    };

    const handleCancel = () => {
      form.resetFields();
      onClose?.();
    };

    React.useImperativeHandle(ref, () => ({
      submit: handleCreate,
      cancel: handleCancel,
    }));

    React.useEffect(() => {
      if (open && isCreateMode) {
        form?.resetFields?.();
      }
    }, [open, isCreateMode, form]);

    React.useEffect(() => {
      if (isCreateMode) return; // skip prefill in create mode
      const info = getSingleDimensionList?.info?.setting || {};
      if (info && Object.keys(info).length > 0) {
        form?.setFieldsValue?.({
          branchId: info.branch_id,
          branchIcon: info.branch_icon,
          branchColor: info.branch_color,
          branchName: info.name,
          description: info.description,
          enableHierarchy: Boolean(info.enable_hierarchy),
          mutuallyExclusive: Boolean(info.mutually_exclusive),
          mandatory: Boolean(info.mandatory),
          country: info.country,
          currency: info.currency,
          timezone: info.time_format || info.timezone,
          language: info.language,
          enableFinancialConsolidation: Boolean(
            info.use_for_financial_consolidation
          ),
          enableAsFilter: Boolean(info.enable_as_filter),
          enableRegionalPerDimension: Boolean(
            info.enable_regional_per_dimension
          ),
        });
      }
    }, [getSingleDimensionList, form, isCreateMode]);

    return (
      <Form form={form} layout="vertical" style={{ width: "100%" }}>
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Card
              title={
                <div
                  style={{
                    borderBottom: themeDefault?.info?.newConfig?.cardborder,
                    paddingBottom: 8,
                  }}
                >
                  General Settings
                </div>
              }
              bordered
            >
              <Flex gap={16} wrap="wrap">
                <Flex align="center" justify="space-between" gap={16}>
                  <Form.Item
                    name="branchId"
                    label="Branch ID"
                    style={{ padding: 0, margin: 0, width: 190 }}
                    rules={[
                      { required: true, message: "Please enter Branch ID" },
                    ]}
                  >
                    <Input
                      placeholder="Enter Branch ID"
                      style={{ width: "100%" }}
                      min={1}
                    />
                  </Form.Item>
                  <Form.Item
                    name="branchIcon"
                    label="Branch Icon"
                    style={{ padding: 0, margin: 0, width: 190 }}
                    rules={[
                      { required: true, message: "Please select an icon" },
                    ]}
                  >
                    <Select placeholder="Select Icon" options={BranchIcons} />
                  </Form.Item>
                  <Form.Item
                    name="branchColor"
                    label="Branch Color"
                    style={{ padding: 0, margin: 0, width: 190 }}
                    rules={[
                      { required: true, message: "Please select a color" },
                    ]}
                  >
                    <Select
                      placeholder="Select Color"
                      options={shiftCodesWithNames.map((color) => ({
                        value: color.code,
                        label: (
                          <Flex align="center" gap={8}>
                            <div
                              style={{
                                width: 24,
                                height: 24,
                                backgroundColor: color.code,
                                borderRadius: "50%",
                              }}
                            />
                            {color.name}
                          </Flex>
                        ),
                      }))}
                    />
                  </Form.Item>
                </Flex>
              </Flex>
              <Form.Item
                name="branchName"
                label="Branch Name"
                style={{ padding: 0, margin: 0, paddingTop: 16, width: 425 }}
                rules={[
                  { required: true, message: "Please enter Branch Name" },
                ]}
              >
                <Input placeholder="Enter Branch Name" />
              </Form.Item>
              <Form.Item
                name="description"
                label="Description"
                style={{ padding: 0, margin: 0, paddingTop: 16 }}
              >
                <TextArea rows={4} placeholder="Enter Description" />
              </Form.Item>
            </Card>

            <Card
              style={{ marginTop: 16 }}
              bordered
              title={
                <div
                  style={{
                    borderBottom: themeDefault?.info?.newConfig?.cardborder,
                    paddingBottom: 8,
                  }}
                >
                  Additional Settings
                </div>
              }
            >
              <div style={{ marginBottom: 16 }}>
                <Flex align="center" gap={8}>
                  <Form.Item
                    name="enableHierarchy"
                    valuePropName="checked"
                    noStyle
                  >
                    <Switch />
                  </Form.Item>
                  <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                    Enable Hierarchy for this dimension
                  </Text>
                </Flex>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    paddingTop: 4,
                    display: "block",
                  }}
                >
                  If enabled, the dimension will have multiple levels
                </Text>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Flex align="center" gap={8}>
                  <Form.Item
                    name="mutuallyExclusive"
                    valuePropName="checked"
                    noStyle
                  >
                    <Switch />
                  </Form.Item>
                  <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                    Employees in this dimension will be mutually exclusive
                  </Text>
                </Flex>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, paddingTop: 4, display: "block" }}
                >
                  Employees can only belong to one level in this dimension
                </Text>
              </div>

              <div>
                <Flex align="center" gap={8}>
                  <Form.Item name="mandatory" valuePropName="checked" noStyle>
                    <Switch />
                  </Form.Item>
                  <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                    Mandatory
                  </Text>
                </Flex>
                <Text
                  type="secondary"
                  style={{ fontSize: 12, paddingTop: 4, display: "block" }}
                >
                  This will make it mandatory to assign this dimension to all
                  employees
                </Text>
              </div>
            </Card>

            {/* <Card
              style={{ marginTop: 16 }}
              bordered
              title={
                <div
                  style={{
                    borderBottom: themeDefault?.info?.newConfig?.cardborder,
                    paddingBottom: 8,
                  }}
                >
                  File Attachments
                </div>
              }
            >
              <Flex vertical gap={8}>
                <Text style={{ fontSize: 16, fontWeight: 700 }}>
                  Upload a file(s) to link with this legal entity
                </Text>
                <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                  Upload the relevant files for the legal entity
                </Text>
                <Button style={{ width: 150 }}>
                  <DownloadDocIcon size="small" />
                  Upload File
                </Button>
                <Text type="secondary" style={{ fontSize: 12, paddingTop: 4 }}>
                  Only .xlsx, .xls, .csv files are supported
                </Text>
              </Flex>
            </Card> */}
          </Col>
          <Col span={12}>
            <Card
              title={
                <Flex
                  align="center"
                  justify="space-between"
                  style={{
                    borderBottom: themeDefault?.info?.newConfig?.cardborder,
                    paddingBottom: 8,
                  }}
                >
                  Regional Settings
                  <Flex gap={4} align="center">
                    <Form.Item
                      name="enableRegionalPerDimension"
                      valuePropName="checked"
                      noStyle
                    >
                      <Switch />
                    </Form.Item>
                    <Text style={{ fontSize: 12, fontWeight: 700 }}>
                      Enable regional settings per dimension
                    </Text>
                  </Flex>
                </Flex>
              }
              bordered
            >
              <Flex gap={16} wrap="wrap">
                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="country"
                    label="Country"
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Select
                      placeholder="Select country"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={countries.map((c) => ({
                        value: c,
                        label: c,
                      }))}
                      onChange={handleCountryChange}
                    />
                  </Form.Item>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, paddingTop: 4 }}
                  >
                    Select the country for the dimension
                  </Text>
                </Flex>

                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="currency"
                    label="Currency"
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Select
                      placeholder="Select currency"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={currencyData.currencies.map((c) => ({
                        value: c.symbol,
                        label: `${c.displayName} (${c.symbol})`,
                      }))}
                    />
                  </Form.Item>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, paddingTop: 4 }}
                  >
                    Select the currency for the whole dimension
                  </Text>
                </Flex>
              </Flex>

              <Flex gap={16} wrap="wrap" style={{ marginTop: 8 }}>
                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="timezone"
                    label="Timezone"
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Select
                      placeholder="Select timezone"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={moment.tz
                        .names()
                        .map((tz) => ({ value: tz, label: tz }))}
                    />
                  </Form.Item>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, paddingTop: 4 }}
                  >
                    Select the timezone for the dimension
                  </Text>
                </Flex>

                <Flex vertical style={{ flex: 1, minWidth: 250 }}>
                  <Form.Item
                    name="language"
                    label="Language"
                    style={{ padding: 0, margin: 0 }}
                  >
                    <Select
                      placeholder="Select Language"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={[
                        { value: "en", label: "English (EN)" },
                        { value: "zh", label: "Chinese (ZH)" },
                        { value: "ar", label: "Arabic (AR)" },
                      ]}
                    />
                  </Form.Item>
                  <Text
                    type="secondary"
                    style={{ fontSize: 12, paddingTop: 4 }}
                  >
                    Select language for the dimension
                  </Text>
                </Flex>
              </Flex>
            </Card>

            <Card
              title={
                <div
                  style={{
                    borderBottom: themeDefault?.info?.newConfig?.cardborder,
                    paddingBottom: 8,
                  }}
                >
                  Dashboards & Reporting
                </div>
              }
              style={{ marginTop: 16 }}
              bordered
            >
              {/* Switch 1 */}
              <Flex align="center" gap={8} style={{ marginBottom: 6 }}>
                <Form.Item
                  name="enableFinancialConsolidation"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch />
                </Form.Item>
                <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                  Use for Financial Consolidation process
                </Text>
              </Flex>
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                }}
              >
                This dimension will have impact on financial reporting
              </Text>

              {/* Switch 2 */}
              <Flex align="center" gap={8} style={{ marginBottom: 6 }}>
                <Form.Item
                  name="enableAsFilter"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch />
                </Form.Item>
                <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                  Enable this entity as a filter across the web portal
                </Text>
              </Flex>
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  marginTop: 0,
                  marginBottom: 0,
                  display: "block",
                }}
              >
                This will enable filtering portal as per the dimensions
              </Text>
            </Card>
          </Col>
        </Row>
      </Form>
    );
  }
);

export default CreateBranchLocation;
